/**
 * PaginationControls Component
 *
 * Renders pagination controls for the TransferList component including:
 * - Page size selector
 * - Page navigation (using Mantine's Pagination component)
 * - Items range information
 */

import { Group, Pagination, Select, Text } from "@mantine/core";

import { cn } from "@/utils/cn";

import { useTransferListPagination } from "./TransferList.hooks";
import classes from "./TransferList.module.css";
import type { TransferListPaginationConfig } from "./TransferList.types";
import { defaultPaginationConfig } from "./TransferList.utils";

/**
 * Props for the PaginationControls component
 */
export interface PaginationControlsProps {
  /** Pagination state and controls from useTransferListPagination hook */
  pagination: ReturnType<typeof useTransferListPagination>;
  /** Pagination configuration with labels and classNames */
  config: TransferListPaginationConfig;
  /** Whether the controls should be disabled */
  disabled?: boolean;
  /** Size variant for the controls */
  size: "xs" | "sm" | "md" | "lg" | "xl";
  /** Theme color for pagination and select (Mantine color) */
  color?: string;
}

/**
 * PaginationControls component
 *
 * Displays pagination controls including page navigation, page size selector,
 * and items range information. Uses Mantine's Pagination component for
 * consistent styling and behavior.
 *
 * @example
 * <PaginationControls
 *   pagination={paginationState}
 *   config={paginationConfig}
 *   disabled={false}
 *   size="md"
 * />
 */
export function PaginationControls({ pagination, config, disabled, size, color }: PaginationControlsProps) {
  // Destructure values from pagination state
  const { active, setPage, totalPages, startIndex, endIndex, totalItems, pageSize, changePageSize } = pagination;

  // Merge custom labels with defaults
  const labels = {
    ...defaultPaginationConfig.labels,
    ...config.labels
  };

  // Merge custom classNames with defaults
  const classNames = {
    ...defaultPaginationConfig.classNames,
    ...config.classNames
  };

  // Format items range text by replacing placeholders
  const itemsRangeText = (labels.itemsRange || "{from}-{to} de {total}")
    .replace("{from}", String(startIndex))
    .replace("{to}", String(endIndex))
    .replace("{total}", String(totalItems));

  return (
    <div className={cn(classes.paginationContainer, classNames.container)} data-size={size} data-disabled={disabled}>
      {/* Top row: Page size selector and Pagination controls */}
      <Group justify="space-between" wrap="wrap" gap="sm">
        {/* Left side: Page size selector */}
        {config.pageSizeOptions && config.pageSizeOptions.length > 0 && (
          <Group gap="xs" className={cn(classes.pageSizeSelector, classNames.pageSizeSelector)}>
            <Text size={size}>{labels.pageSizeLabel || "Itens por página:"}</Text>
            <Select
              size={size}
              value={String(pageSize)}
              onChange={(value) => value && changePageSize(Number(value))}
              data={config.pageSizeOptions.map((opt) => ({
                value: String(opt),
                label: String(opt)
              }))}
              disabled={disabled}
              className={classNames.pageSizeSelect}
              w={80}
              comboboxProps={{ withinPortal: false }}
              clearable={false}
              color={color}
            />
          </Group>
        )}

        {/* Right side: Mantine Pagination component */}
        <Pagination
          total={totalPages}
          value={active}
          onChange={setPage}
          disabled={disabled}
          size={size}
          siblings={1}
          boundaries={1}
          className={classNames.pagination}
          color={color}
        />
      </Group>

      {/* Bottom row: Items range info */}
      <Text size={size} c="dimmed" className={cn(classes.itemsRange, classNames.itemsRange, "flex justify-end")} mt="xs">
        {itemsRangeText}
      </Text>
    </div>
  );
}
